// Security Manager for implementing security measures
export class SecurityManager {
  static initialize() {
    this.securityHeaders = {
      'Content-Security-Policy': "default-src 'self'; script-src 'self' 'unsafe-inline'; style-src 'self' 'unsafe-inline'; img-src 'self' data: https:; connect-src 'self' https://api.stripe.com https://www.google-analytics.com;",
      'X-Frame-Options': 'DENY',
      'X-Content-Type-Options': 'nosniff',
      'X-XSS-Protection': '1; mode=block',
      'Referrer-Policy': 'strict-origin-when-cross-origin'
    };
    
    this.rateLimiting = {
      enabled: true,
      maxRequests: 100,
      windowMs: 60000, // 1 minute
      requests: new Map()
    };
    
    this.sanitizationRules = {
      allowedTags: ['b', 'i', 'em', 'strong', 'p', 'br'],
      allowedAttributes: {}
    };
    
    console.log('SecurityManager initialized');
  }

  // Sanitize user input to prevent XSS
  static sanitizeInput(input) {
    if (typeof input !== 'string') return input;
    
    // Remove potentially dangerous characters
    return input
      .replace(/[<>]/g, '') // Remove < and >
      .replace(/javascript:/gi, '') // Remove javascript: protocol
      .replace(/on\w+=/gi, '') // Remove event handlers
      .trim();
  }

  // Validate and sanitize calculator inputs
  static validateCalculatorInputs(inputs) {
    const sanitized = {};
    const errors = [];
    
    for (const [key, value] of Object.entries(inputs)) {
      if (typeof value === 'string') {
        sanitized[key] = this.sanitizeInput(value);
        
        // Check for suspicious patterns
        if (this.detectSuspiciousInput(value)) {
          errors.push(`Suspicious input detected in field: ${key}`);
        }
      } else {
        sanitized[key] = value;
      }
    }
    
    return {
      sanitized,
      errors,
      isValid: errors.length === 0
    };
  }

  // Detect suspicious input patterns
  static detectSuspiciousInput(input) {
    if (typeof input !== 'string') return false;
    
    const suspiciousPatterns = [
      /<script/i,
      /javascript:/i,
      /on\w+\s*=/i,
      /eval\s*\(/i,
      /expression\s*\(/i,
      /vbscript:/i,
      /data:text\/html/i
    ];
    
    return suspiciousPatterns.some(pattern => pattern.test(input));
  }

  // Rate limiting check
  static checkRateLimit(identifier = 'default') {
    if (!this.rateLimiting.enabled) return true;
    
    const now = Date.now();
    const windowStart = now - this.rateLimiting.windowMs;
    
    // Clean old entries
    const requests = this.rateLimiting.requests.get(identifier) || [];
    const validRequests = requests.filter(timestamp => timestamp > windowStart);
    
    // Check if limit exceeded
    if (validRequests.length >= this.rateLimiting.maxRequests) {
      return false;
    }
    
    // Add current request
    validRequests.push(now);
    this.rateLimiting.requests.set(identifier, validRequests);
    
    return true;
  }

  // Generate CSRF token
  static generateCSRFToken() {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }

  // Validate CSRF token
  static validateCSRFToken(token, sessionToken) {
    // In a real implementation, this would validate against server-side token
    return token && token.length === 64;
  }

  // Hash sensitive data
  static hashData(data) {
    // Simple hash function for non-cryptographic purposes
    let hash = 0;
    const str = JSON.stringify(data);
    
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    
    return Math.abs(hash).toString(16);
  }

  // Encrypt sensitive data (client-side encryption for demo)
  static encryptData(data, key = 'default-key') {
    try {
      // Simple encryption for demo purposes
      // In production, use proper encryption libraries
      const jsonString = JSON.stringify(data);
      let encrypted = '';
      
      for (let i = 0; i < jsonString.length; i++) {
        const charCode = jsonString.charCodeAt(i) ^ key.charCodeAt(i % key.length);
        encrypted += String.fromCharCode(charCode);
      }
      
      return btoa(encrypted);
    } catch (error) {
      console.error('Encryption error:', error);
      return null;
    }
  }

  // Decrypt sensitive data
  static decryptData(encryptedData, key = 'default-key') {
    try {
      const encrypted = atob(encryptedData);
      let decrypted = '';
      
      for (let i = 0; i < encrypted.length; i++) {
        const charCode = encrypted.charCodeAt(i) ^ key.charCodeAt(i % key.length);
        decrypted += String.fromCharCode(charCode);
      }
      
      return JSON.parse(decrypted);
    } catch (error) {
      console.error('Decryption error:', error);
      return null;
    }
  }

  // Security audit log
  static logSecurityEvent(event, details = {}) {
    const logEntry = {
      timestamp: new Date().toISOString(),
      event,
      details,
      userAgent: navigator.userAgent,
      url: window.location.href,
      ip: 'client-side' // Would be server IP in production
    };
    
    console.warn('Security Event:', logEntry);
    
    // Track security events
    if (window.AnalyticsManager) {
      window.AnalyticsManager.trackEvent('security_event', {
        event,
        severity: details.severity || 'info'
      });
    }
  }

  // Check for security vulnerabilities
  static performSecurityCheck() {
    const checks = {
      https: window.location.protocol === 'https:',
      csp: document.querySelector('meta[http-equiv="Content-Security-Policy"]') !== null,
      xFrameOptions: document.querySelector('meta[http-equiv="X-Frame-Options"]') !== null,
      secureCookies: document.cookie.includes('Secure'),
      noInlineScripts: !document.querySelector('script:not([src])')
    };
    
    const score = Object.values(checks).filter(Boolean).length;
    const total = Object.keys(checks).length;
    
    return {
      checks,
      score,
      total,
      percentage: Math.round((score / total) * 100)
    };
  }

  // Get security headers
  static getSecurityHeaders() {
    return this.securityHeaders;
  }

  // Update security configuration
  static updateSecurityConfig(config) {
    if (config.rateLimiting) {
      this.rateLimiting = { ...this.rateLimiting, ...config.rateLimiting };
    }
    
    if (config.sanitizationRules) {
      this.sanitizationRules = { ...this.sanitizationRules, ...config.sanitizationRules };
    }
  }
}